"use client";

import * as React from "react";
import { Button } from "./Button";

// Loading spinner and rich text content styles
const EMBEDDED_HTML_STYLES = `
  @keyframes embedded-html-spin {
    to { transform: rotate(360deg); }
  }
  .embedded-html-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 40px;
  }
  .embedded-html-loader-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #f3f4f6;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: embedded-html-spin 1s linear infinite;
  }
  .embedded-html-loader-text {
    margin-top: 16px;
    color: #6b7280;
    font-size: 14px;
  }

  .otnotice-section-header {
    direction: ltr;
    text-align: left !important;
    color: var(--typography-colors-text-body, #242224) !important;
    font-family: var(--font-family-sans) !important;
    font-size: var(--typography-sizes-headline-2, 56px) !important;
    font-style: normal;
    font-weight: 500 !important;
    line-height: 110% !important;
    letter-spacing: -1.12px !important;
  }
  .otnotice-section-content {
    direction: ltr;
    text-align: left !important;
  }

  .otnotice-section-content h2 {
    color: var(--typography-colors-text-body, #242224) !important;
    font-family: var(--font-family-sans) !important;
    font-size: var(--typography-sizes-headline-2, 56px) !important;
    font-style: normal;
    font-weight: 500 !important;
    line-height: 110% !important;
    letter-spacing: -1.12px !important;
  }

  .otnotice-section-content p {
    color: var(--typography-colors-text-body, #242224) !important;
    font-family: var(--font-family-sans) !important;
    font-size: var(--typography-sizes-body-medium, 16px) !important;
    font-style: normal !important;
    font-weight: 500 !important;
    line-height: var(--typography-line-heights-body-medium, 26px) !important;
  }

  .otnotice-section-content a {
    color: var(--typography-colors-text-body, #242224) !important;
    font-family: var(--font-family-sans) !important;
    font-size: var(--typography-sizes-body-medium, 16px) !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: var(--typography-line-heights-link, 26px) !important;
    text-decoration-line: underline !important;
    text-decoration-style: solid !important;
    text-decoration-skip-ink: none !important;
    text-decoration-thickness: auto !important;
    text-underline-offset: auto !important;
    text-underline-position: from-font !important;
  }

  .otnotice:not(:empty) ~ .embedded-html-loader,
  .ot-privacy-notice-language-dropdown-container:not(:empty) ~ .embedded-html-loader,
  #ot-sdk-cookie-policy:not(:empty) ~ .embedded-html-loader {
    display: none;
  }

  /* Hide menu for terms-of-use variant when only one item is present */
  #otnotice-980aeffd-f77b-44fa-a63c-a54cc2fc7392 .otnotice-menu,
  #otnotice-980aeffd-f77b-44fa-a63c-a54cc2fc7392 .otnotice-menu-mobile {
    display: none;
  }

  /* Adjust content layout when menu is hidden */
  #otnotice-980aeffd-f77b-44fa-a63c-a54cc2fc7392 .otnotice-content {
    margin-left: 0 !important;
    max-width: 100% !important;
  }

  /* Remove right margin for sections in terms-of-use */
  #otnotice-980aeffd-f77b-44fa-a63c-a54cc2fc7392 .otnotice-sections {
    margin-right: 0 !important;
  }

  /* Align text to left for all OneTrust content */
  .otnotice,
  .ot-privacy-notice-language-dropdown-container {
    text-align: left;
  }
`;

// OneTrust Privacy Notice scripts for ParkMobile
// Maps to the "type" field in Storyblok's embeddedHTML component
export const PREDEFINED_SCRIPTS = {
  "terms-of-use": `
    <!-- OneTrust Privacy Notice start -->
    <!-- Container in which the Privacy Notice will be rendered -->
    <div id="otnotice-980aeffd-f77b-44fa-a63c-a54cc2fc7392" class="otnotice"></div>

    <script src="https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js" type="text/javascript" charset="UTF-8" id="otprivacy-notice-script">
    settings="eyJjYWxsYmFja1VybCI6Imh0dHBzOi8vcGFya21vYmlsZS1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL3ByaXZhY3lOb3RpY2VzL3N0YXRzL3ZpZXdzIiwiY29udGVudEFwaVVybCI6Imh0dHBzOi8vcGFya21vYmlsZS1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL2VudGVycHJpc2Vwb2xpY3kvZGlnaXRhbHBvbGljeS9jb250ZW50IiwibWV0YWRhdGFBcGlVcmwiOiJodHRwczovL3Bhcmttb2JpbGUtcHJpdmFjeS5teS5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9lbnRlcnByaXNlcG9saWN5L2RpZ2l0YWxwb2xpY3kvbWV0YS1kYXRhIn0="
    </script>

    <script type="text/javascript" charset="UTF-8">
    // To ensure external settings are loaded, use the Initialized promise:
    OneTrust.NoticeApi.Initialized.then(function() {
    OneTrust.NoticeApi.LoadNotices(["https://privacyportal-cdn.onetrust.com/storage-container/27d9029b-6fdd-4c36-8971-2cf0c03cd05f/privacy-notices/980aeffd-f77b-44fa-a63c-a54cc2fc7392/published/privacynotice.json"]);
    });
    </script>

    <!-- OneTrust Privacy Notice end -->
  `,
  "privacy-policy": `
    <!-- OneTrust Privacy Notice start -->
    <!-- Language Drop-down element that will control in which language the Privacy Notice is displayed -->
    <div class="ot-privacy-notice-language-dropdown-container"></div>
    <!-- Container in which the Privacy Notice will be rendered -->
    <div id="otnotice-2770dd75-cba3-469a-a889-48a71c323f06" class="otnotice"></div>

    <script src="https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js" type="text/javascript" charset="UTF-8" id="otprivacy-notice-script">
    settings="eyJjYWxsYmFja1VybCI6Imh0dHBzOi8vcGFya21vYmlsZS1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL3ByaXZhY3lOb3RpY2VzL3N0YXRzL3ZpZXdzIiwiY29udGVudEFwaVVybCI6Imh0dHBzOi8vcGFya21vYmlsZS1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL2VudGVycHJpc2Vwb2xpY3kvZGlnaXRhbHBvbGljeS9jb250ZW50IiwibWV0YWRhdGFBcGlVcmwiOiJodHRwczovL3Bhcmttb2JpbGUtcHJpdmFjeS5teS5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9lbnRlcnByaXNlcG9saWN5L2RpZ2l0YWxwb2xpY3kvbWV0YS1kYXRhIn0="
    </script>

    <script type="text/javascript" charset="UTF-8">
    // To ensure external settings are loaded, use the Initialized promise:
    OneTrust.NoticeApi.Initialized.then(function() {
    OneTrust.NoticeApi.LoadNotices(["https://privacyportal-cdn.onetrust.com/storage-container/27d9029b-6fdd-4c36-8971-2cf0c03cd05f/privacy-notices/2770dd75-cba3-469a-a889-48a71c323f06/published/privacynotice.json"]);
    });
    </script>

    <!-- OneTrust Privacy Notice end -->
  `,
  accessibility: `
    <!-- OneTrust Privacy Notice start -->
    <!-- Language Drop-down element that will control in which language the Privacy Notice is displayed -->
    <div class="ot-privacy-notice-language-dropdown-container"></div>
    <!-- Container in which the Privacy Notice will be rendered -->
    <div id="otnotice-13ba335c-862b-4392-8572-e199e2b1e91b" class="otnotice"></div>

    <script src="https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js" type="text/javascript" charset="UTF-8" id="otprivacy-notice-script">
    settings="eyJjYWxsYmFja1VybCI6Imh0dHBzOi8vcGFya21vYmlsZS1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL3ByaXZhY3lOb3RpY2VzL3N0YXRzL3ZpZXdzIiwiY29udGVudEFwaVVybCI6Imh0dHBzOi8vcGFya21vYmlsZS1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL2VudGVycHJpc2Vwb2xpY3kvZGlnaXRhbHBvbGljeS9jb250ZW50IiwibWV0YWRhdGFBcGlVcmwiOiJodHRwczovL3Bhcmttb2JpbGUtcHJpdmFjeS5teS5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9lbnRlcnByaXNlcG9saWN5L2RpZ2l0YWxwb2xpY3kvbWV0YS1kYXRhIn0="
    </script>

    <script type="text/javascript" charset="UTF-8">
    // To ensure external settings are loaded, use the Initialized promise:
    OneTrust.NoticeApi.Initialized.then(function() {
    OneTrust.NoticeApi.LoadNotices(["https://privacyportal-cdn.onetrust.com/storage-container/27d9029b-6fdd-4c36-8971-2cf0c03cd05f/privacy-notices/13ba335c-862b-4392-8572-e199e2b1e91b/published/privacynotice.json"]);
    });
    </script>

    <!-- OneTrust Privacy Notice end -->
  `,
  "cookie-policy": `
    <!-- OneTrust cookies list start -->
    <div id="ot-sdk-cookie-policy"></div>
    <!-- OneTrust cookies list end -->
  `,
} as const;

export type PredefinedScript = keyof typeof PREDEFINED_SCRIPTS;

export type EmbeddedHTMLProps = {
  /**
   * Predefined OneTrust script to load
   */
  predefinedScript?: PredefinedScript;
  /**
   * Optional HTML content to prepend before the script
   */
  html?: string;
};

export const EmbeddedHTML = React.forwardRef<HTMLDivElement, EmbeddedHTMLProps>(
  ({ html = "", predefinedScript }, ref) => {
    const scriptContent = predefinedScript
      ? PREDEFINED_SCRIPTS[predefinedScript]
      : "";

    // Add loader that will be hidden once OneTrust content loads (not for cookie-policy)
    const loaderHTML = predefinedScript && predefinedScript !== "cookie-policy"
      ? `
      <div class="embedded-html-loader">
        <div class="embedded-html-loader-spinner"></div>
        <div class="embedded-html-loader-text">Loading content...</div>
      </div>
    `
      : "";

    const finalHTML = html + scriptContent + loaderHTML;

    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: EMBEDDED_HTML_STYLES }} />
        {predefinedScript === "cookie-policy" && (
          <Button
            variant="tertiary"
            size="small"
            id="ot-sdk-btn"
            className="ot-sdk-show-settings uipm:mb-8"
          >
            Cookie settings
          </Button>
        )}
        <div ref={ref} dangerouslySetInnerHTML={{ __html: finalHTML }} />
      </>
    );
  },
);

EmbeddedHTML.displayName = "EmbeddedHTML";
